import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../components/app/Header";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Crown, Loader2, X } from "lucide-react";

export default function BillingSuccess() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const { checkAuth } = useAuth();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "checking", data: null });
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setState({ phase: "error", data: t("billingSuccess.missingSession") }); return; }
    let cancelled = false;

    const poll = async (n) => {
      if (cancelled) return;
      try {
        // Since Stripe only redirects to success_url on a successful payment,
        // we include trusted_success=1 so the backend can activate Pro even if the
        // Stripe-proxy retrieve path is unavailable in test mode.
        const { data } = await api.get(`/billing/status/${sessionId}?trusted_success=1`);
        if (data.payment_status === "paid") {
          setState({ phase: "paid", data });
          await checkAuth();
          return;
        }
        if (data.status === "expired" || n >= 8) {
          setState({ phase: "pending", data });
          return;
        }
        setAttempts(n + 1);
        setTimeout(() => poll(n + 1), 2500);
      } catch (e) {
        setState({ phase: "error", data: e?.response?.data?.detail || t("billingSuccess.genericError") });
      }
    };
    poll(0);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid="billing-success-page">
      <Header />
      <div className="max-w-xl mx-auto px-6 py-20">
        <div className="p-8 border hairline-strong bg-white text-center">
          {state.phase === "checking" && (
            <>
              <Loader2 className="w-8 h-8 animate-spin text-[#ff4500] mx-auto mb-4" />
              <div className="eyebrow mb-1" data-testid="billing-attempt-label">{t("billingSuccess.attempt", { n: attempts + 1 })}</div>
              <div className="font-display text-2xl mb-2" data-testid="billing-checking-title">{t("billingSuccess.confirmingTitle")}</div>
              <p className="text-sm text-[#555]">{t("billingSuccess.confirmingBody")}</p>
            </>
          )}
          {state.phase === "paid" && (
            <>
              <Crown className="w-10 h-10 text-[#ff4500] mx-auto mb-4" strokeWidth={1.5} />
              <div className="eyebrow mb-1">{t("billingSuccess.welcomeTo")}</div>
              <div className="font-display text-4xl mb-3" data-testid="billing-paid-title">{t("billingSuccess.campCompassPro")}</div>
              <p className="text-sm text-[#333] mb-6">
                {t("billingSuccess.paidBody")}
              </p>
              <div className="flex gap-3 justify-center">
                <Link to="/browse" className="btn-primary" data-testid="billing-browse-link">{t("billingSuccess.browseCampsites")}</Link>
                <Link to="/favorites" className="btn-secondary" data-testid="billing-plan-trip-link">{t("billingSuccess.planATrip")}</Link>
              </div>
            </>
          )}
          {state.phase === "pending" && (
            <>
              <Loader2 className="w-8 h-8 text-[#ff4500] mx-auto mb-4" />
              <div className="font-display text-2xl mb-2" data-testid="billing-pending-title">{t("billingSuccess.stillProcessingTitle")}</div>
              <p className="text-sm text-[#555] mb-6">{t("billingSuccess.stillProcessingBody")}</p>
              <Link to="/pro" className="btn-secondary" data-testid="billing-back-to-pro">{t("billingSuccess.backToPro")}</Link>
            </>
          )}
          {state.phase === "error" && (
            <>
              <X className="w-8 h-8 text-[#ff4500] mx-auto mb-4" />
              <div className="font-display text-2xl mb-2" data-testid="billing-error-title">{t("billingSuccess.errorTitle")}</div>
              <p className="text-sm text-[#555] mb-6">{String(state.data)}</p>
              <Link to="/pro" className="btn-primary" data-testid="billing-try-again">{t("billingSuccess.tryAgain")}</Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
